import { cleanDocument } from '~/helpers/documentNumber'
import { IRecoverPasswordForm } from '~/schemas/auth/authFormSchemas'
import {
    IRecoverPasswordRequest,
    IRecoveryTokenResponse,
    RecoveryTokenResponseSchema,
    TextMessageSchema,
} from '~/schemas/auth/authSchemas'
import { api, apiService } from '~/services/core/ApiService'

/*
 * Recuperação de senha: link (esqueci minha senha) e troca pelo link. A troca com o cliente logado é do domínio
 * customer (`changePasswordService` em services/domain/customer/CustomerService.ts). Erros sem aviso padrão em todos:
 * a tela decide (modal, erro de campo pelo useFormAction ou aviso genérico).
 */

const silent = { useDefaultErrorHandler: false } as const

/** Todas as chamadas de senha mostram o overlay de carregamento. */
const withLoader = { meta: { showLoader: true } }

/** O token do link vai no Authorization da chamada; o interceptor não troca pelo da sessão nem a encerra num 401. */
const withRecoveryToken = (token: string) => ({ ...withLoader, headers: { Authorization: `Bearer ${token}` } })

// =========================================================================
// SECTION 1: ESQUECI MINHA SENHA
// =========================================================================

/** `POST /auth/request-password-link`. A API responde o mesmo texto exista ou não a conta. */
export const requestPasswordLinkByEmailService = (email: string): Promise<string> =>
    apiService(api)
        .post('/auth/request-password-link', { email }, TextMessageSchema, withLoader)
        .onError(() => {}, silent)
        .execute()

/** `POST /auth/request-password-link-by-maindoc`: CPF ou CNPJ sem máscara; o texto traz o e-mail mascarado. */
export const requestPasswordLinkByDocumentService = (document: string): Promise<string> =>
    apiService(api)
        .post(
            '/auth/request-password-link-by-maindoc',
            { MainDocument: cleanDocument(document) },
            TextMessageSchema,
            withLoader,
        )
        .onError(() => {}, silent)
        .execute()

// =========================================================================
// SECTION 2: NOVA SENHA PELO LINK
// =========================================================================

/** `GET /auth/change-password`: o link vale e é deste e-mail. 401 é link vencido ou inválido. */
export const verifyRecoveryTokenService = (token: string): Promise<IRecoveryTokenResponse> =>
    apiService(api)
        .get('/auth/change-password', RecoveryTokenResponseSchema, withRecoveryToken(token))
        .onError(() => {}, silent)
        .execute()

/** `PUT /auth/change-password`: troca a senha da conta do link. */
export const recoverPasswordService = (token: string, form: IRecoverPasswordForm): Promise<string> => {
    const payload: IRecoverPasswordRequest = {
        newPassword: form.newPassword,
        newPasswordConfirm: form.newPasswordConfirm,
    }
    return apiService(api)
        .put('/auth/change-password', payload, TextMessageSchema, withRecoveryToken(token))
        .onError(() => {}, silent)
        .execute()
}

/** O `?t=` do link é o token de recuperação em base64. Valor que não decodifica devolve `null`. */
export function decodeRecoveryToken(encoded: string): string | null {
    try {
        return atob(encoded)
    } catch {
        return null
    }
}
